import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { RHFTextField } from 'src/components/hook-form';
import { UploadErrorMessage } from 'src/components/hook-form/UploadErrorMessage';
import RHFUploadImage from 'src/components/hook-form/uploadImage/RHFUploadImage';
import Iconify from 'src/components/iconify';
import useResponsive from 'src/hooks/useResponsive';
import {
  DeleteButton,
  FormInnerContainer,
  FormItem,
  FormLabel,
  FormRow,
  ImageContainer,
} from 'src/layouts/form/FormLayout';
import { getFileUrl } from 'src/utils/fileUploadUtils';

import { useCompanyInfoByLanguage } from './hooks/useCompanyInfoByLangCd';

interface ILocationProps {
  langCd: string;
}

export const Location = ({ langCd }: ILocationProps) => {
  const { t } = useTranslation();
  const { formState, setValue } = useFormContext();
  const isDesktopXl = useResponsive('up', 'xl');
  // 언어에 해당하는 index와 세부 정보 반환
  const { index, selectedCompanyInfo } = useCompanyInfoByLanguage(langCd);

  // 현재 파일과 경로 값을 실시간으로 감시
  const currentFile = selectedCompanyInfo?.locationImgFile;
  const currentPath = selectedCompanyInfo?.locationImgPath;

  return (
    <FormInnerContainer>
      <FormLabel>{t('settings.form.location.name')}</FormLabel>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isDesktopXl ? 'row' : 'column',
          gap: 4,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <FormInnerContainer>
          <FormLabel>{t('settings.form.location.locationImg')}</FormLabel>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
              {/* 이미지가 있을 경우 보여주기 */}
              {currentFile || currentPath ? (
                <ImageContainer sx={{ width: 150, height: 150 }}>
                  <img
                    src={
                      currentFile
                        ? URL.createObjectURL(currentFile)
                        : getFileUrl(currentPath as string)
                    }
                    alt="location"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <DeleteButton
                    onClick={() => {
                      if (index !== -1) {
                        setValue(`companyInfos.${index}.locationImgFile`, null);
                        setValue(`companyInfos.${index}.locationImgPath`, null);
                      }
                    }}
                  >
                    <Iconify icon="eva:close-fill" width={20} />
                  </DeleteButton>
                </ImageContainer>
              ) : (
                /* 이미지 업로드 컴포넌트 */
                <RHFUploadImage
                  name={`companyInfos.${index}.locationImgFile`}
                  files={[]}
                  onChange={(files) => {
                    if (files[0]) {
                      setValue(`companyInfos.${index}.locationImgFile`, files[0]);
                      setValue(`companyInfos.${index}.locationImgPath`, null);
                    }
                  }}
                  accept={{ 'image/*': [] }}
                  maxFiles={1}
                />
              )}

              {formState.errors?.locationImgFile?.message && (
                <UploadErrorMessage message={String(formState.errors?.locationImgFile?.message)} />
              )}
            </Box>
          </FormRow>
        </FormInnerContainer>
        <Box sx={{ width: '100%' }}>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFTextField
                name={`companyInfos.${index}.email`}
                label={t('settings.form.location.email')}
              />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFTextField
                name={`companyInfos.${index}.salesInquiry`}
                label={t('settings.form.location.sales')}
              />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFTextField
                name={`companyInfos.${index}.recruitmentInquiry`}
                label={t('settings.form.location.recruitment')}
              />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFTextField
                name={`companyInfos.${index}.link`}
                label={t('settings.form.location.link')}
              />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFTextField
                name={`companyInfos.${index}.descript`}
                label={t(`settings.form.location.descript${langCd === 'en' ? '(en)' : '(ko)'}`)}
                multiline
                rows={3}
              />
            </FormItem>
          </FormRow>
        </Box>
      </Box>
    </FormInnerContainer>
  );
};

export default observer(Location);
